import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { loginState } from 'global-stores';
import {
	Drawer,
	DrawerHead,
	KV,
	Section,
} from 'views/design-system';
import { useSignDocHistory, useGetSignDocHistory } from 'views/sign-doc-history';
import { ISignedDocInbox, useSignedDocsInbox } from 'views/signed-doc-inbox';

import { CHIP, actionOf, dayTimeOf, explain, pageCount } from './derive';

const nameOf = (r: any) => r?.fullName || r?.name || r?.email || 'Recipient';

const RECIPIENT_CHIP: Record<string, { cls: string; label: string }> = {
	completed: { cls: 'ok', label: 'SIGNED' },
	pending: { cls: 'wait', label: 'PENDING' },
	underReview: { cls: 'rev', label: 'UNDER REVIEW' },
	approved: { cls: 'ok', label: 'APPROVED' },
};

/**
 * Split out so the history hook, which keys off one envelope, is never asked to
 * render with a null one.
 */
const EnvelopeBody: FC<{
	envelope: ISignedDocInbox;
	chip: { cls: string; label: string };
	why: { text: string; tone: string } | null;
	busy: boolean;
	onClose: () => void;
	onResend: () => void;
	onDownload: () => void;
	onVoid: () => void;
	/** Present only while this envelope is waiting on the signed-in user. */
	onSign?: () => void;
}> = ({
	envelope,
	chip,
	why,
	busy,
	onClose,
	onResend,
	onDownload,
	onVoid,
	onSign,
}) => {
	const { activityLogs } = useGetSignDocHistory(envelope);

	const recipients = envelope.recipients ?? [];
	const done = recipients.filter(r => r.status === 'completed').length;
	const pages = pageCount(envelope);

	/*
	 * IS THERE A SIGNED PDF TO DOWNLOAD AT ALL?
	 *
	 * `GET /envelope/:id` only answers with a `url` once the document has been
	 * produced, which happens when the last recipient signs. Offered
	 * unconditionally, Download on a WAITING FOR OTHERS envelope fired the
	 * request anyway, took a 400, and surfaced it as the generic "Failed to
	 * download the document" — so the drawer told the user, in the same breath,
	 * that it was waiting on someone AND that downloading had failed.
	 *
	 * Every recipient completed is the condition, not `status === 'completed'`
	 * alone: an envelope that everybody has signed sits in `configured` until
	 * compliance review clears it (`actionOf` reads that as UNDER REVIEW), and
	 * its PDF exists throughout. The status check stays as the second arm for an
	 * envelope the API has closed without a recipient list.
	 */
	const canDownload =
		(recipients.length > 0 && done === recipients.length) ||
		envelope.status === 'completed';

	return (
		<>
			<DrawerHead title={envelope.name || 'Envelope'} onClose={onClose}>
				<div className="dsteps">
					<span className={`vch ${chip.cls}`}>{chip.label}</span> &nbsp;
					{recipients.map(r => (
						<span key={r._id ?? r.email}>
							<i
								className={
									r.status === 'completed'
										? 'dot-ok'
										: r.status === 'pending'
										? 'dot-pend'
										: 'dot-rev'
								}
							/>
							{nameOf(r)} ({r.status === 'completed' ? 'signed' : r.status}) &nbsp;
						</span>
					))}
				</div>
				<div className="dsteps">
					purpose : {envelope.purpose ?? '--'} &nbsp;&middot;&nbsp; authType :{' '}
					{envelope.authType ?? '--'} &nbsp;&middot;&nbsp; origin : {envelope.origin ?? '--'}
				</div>
				<div className="dbtns">
					{/* The drawer said "Your signature is required on this packet" and
					    offered no way to give it. When it is this user's turn, signing
					    is the primary action and Resend steps down to a plain button —
					    `.dbtn.pri` is the one emphasis in this strip. */}
					{onSign && (
						<button className="dbtn pri" type="button" onClick={onSign}>
							Sign
						</button>
					)}
					<button
						className={onSign ? 'dbtn' : 'dbtn pri'}
						type="button"
						disabled={busy}
						onClick={onResend}
					>
						Resend Invitation
					</button>
					<button
						className="dbtn"
						type="button"
						disabled={busy || !canDownload}
						onClick={onDownload}
						/* A disabled control with no reason on it is just a dead
						   button. The title is what the drawer would otherwise
						   have made the user infer from the recipients table. */
						title={
							canDownload
								? undefined
								: 'Available once every recipient has signed'
						}
					>
						Download Signed PDF
					</button>
					{envelope.status !== 'voided' && (
						<button className="dbtn danger" type="button" onClick={onVoid}>
							Void
						</button>
					)}
				</div>
			</DrawerHead>

			{why && (
				<div className="why" style={{ margin: '12px 18px' }}>
					{why.text}
				</div>
			)}

			<Section
				title="Documents"
				right={`${envelope.documents?.length ?? 0} document${
					(envelope.documents?.length ?? 0) === 1 ? '' : 's'
				} · ${pages} page${pages === 1 ? '' : 's'}`}
			>
				<KV
					rows={(envelope.documents ?? []).map((d: any) => [
						d?.document?.name ?? 'Document',
						`${d?.document?.pages?.length ?? 0} pages · ${(d?.tabs ?? []).length} tabs`,
					])}
				/>
			</Section>

			<Section title="Recipients" right={`${done} of ${recipients.length} completed`}>
				{recipients.map(r => {
					const rc = RECIPIENT_CHIP[r.status] ?? { cls: 'ns', label: String(r.status) };
					return (
						<div className="rcp" key={r._id ?? r.email}>
							<div className="rt">
								<span className="rn2">{nameOf(r)}</span>
								<span className="rm">{r.email}</span>
								<span className={`vch ${rc.cls}`}>{rc.label}</span>
							</div>
							<div className="tl2">
								<i className={r.sentDateTime ? 'dot-ok' : 'dot-pend'} />
								Sent {r.sentDateTime ? dayTimeOf(r.sentDateTime) : '—'} &nbsp;
								<i className={r.status === 'completed' ? 'dot-ok' : 'dot-pend'} />
								Signed{' '}
								{(r as any)?.completedAt ? dayTimeOf((r as any).completedAt) : '—'}
							</div>
							<div className="note">
								{(r as any)?.consent
									? 'Agreed to the Electronic Record & Signature Disclosure'
									: 'Has not agreed to the disclosure'}
								{r.role ? ` · ${r.role}` : ''}
								{(r as any)?.action ? ` · ${(r as any).action}` : ''}
							</div>
						</div>
					);
				})}
				{recipients.length === 0 && (
					<div className="sv2-empty">No recipients on this envelope yet.</div>
				)}
			</Section>

			<Section title="Envelope History">
				<table className="atbl">
					<thead>
						<tr>
							<th>Time</th>
							<th>User</th>
							<th>Actions</th>
							<th>Activity</th>
						</tr>
					</thead>
					<tbody>
						{activityLogs.map(log => (
							<tr key={log._id}>
								<td>{dayTimeOf(log.createdAt)}</td>
								<td>{log.userName}</td>
								<td>
									<b>{log.action}</b>
								</td>
								<td>{log.activity}</td>
							</tr>
						))}
					</tbody>
				</table>
				{activityLogs.length === 0 && (
					<div className="sv2-empty">No history recorded yet.</div>
				)}
			</Section>
		</>
	);
};

interface IEnvelopeDrawer {
	envelope: ISignedDocInbox | null;
	open: boolean;
	onClose: () => void;
	onChanged: () => void;
	/**
	 * Launch the signing widget on this envelope. Owned by the deck, not the
	 * drawer, so the row, the kebab and this button all open the SAME modal.
	 */
	onSign?: (envelope: ISignedDocInbox) => void;
}

export const EnvelopeDrawer: FC<IEnvelopeDrawer> = ({
	envelope,
	open,
	onClose,
	onChanged,
	onSign,
}) => {
	const { email } = useRecoilValue(loginState) ?? {};
	const { resendSignDocInbox, downloadSignDocInbox, voidPacket } = useSignedDocsInbox();
	const { fetchHistory } = useSignDocHistory();

	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (open && envelope?._id) fetchHistory(envelope._id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, envelope?._id]);

	const action = useMemo(
		() => (envelope ? actionOf(envelope, email) : 'configured'),
		[envelope, email]
	);
	const chip = CHIP[action];
	const why = useMemo(
		() => (envelope ? explain(envelope, action) : null),
		[envelope, action]
	);

	const resend = useCallback(() => {
		if (envelope) resendSignDocInbox(envelope._id, setBusy);
	}, [envelope, resendSignDocInbox]);

	const download = useCallback(() => {
		if (envelope) downloadSignDocInbox(envelope._id, setBusy);
	}, [envelope, downloadSignDocInbox]);

	const sign = useCallback(() => {
		if (envelope && onSign) onSign(envelope);
	}, [envelope, onSign]);

	const voidIt = useCallback(async () => {
		if (!envelope) return;
		// eslint-disable-next-line no-alert
		const reason = window.prompt('Void this packet — reason for the audit trail:');
		if (!reason) return;
		await voidPacket(envelope._id, { status: 'voided', statusMessage: reason });
		onChanged();
	}, [envelope, voidPacket, onChanged]);

	if (!envelope) return <Drawer open={false} label="Envelope details" />;

	return (
		<Drawer open={open} label="Envelope details">
			<EnvelopeBody
				envelope={envelope}
				chip={chip}
				why={why}
				busy={busy}
				onClose={onClose}
				onResend={resend}
				onDownload={download}
				onVoid={voidIt}
				onSign={action === 'needToSign' && onSign ? sign : undefined}
			/>
		</Drawer>
	);
};
